// src/accounts/components/RegisterAccountModal.jsx
//
// "Register Account" flow, launched from OrgSwitcher's "Add account" row
// and from the empty state / header action on AccountsPage. Also doubles
// as the "Rename Account" flow (pass `editAccount`) — same form either
// way, since the real backend contract for both create
// (POST /api/accounts/) and update (PUT /api/accounts/{id}) is just
// `{ name }`.

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2 } from "lucide-react";
import { Modal, FormField, Input, Button, Alert, useDiscardConfirm } from "../../shared/ui";

const initialValues = { name: "" };

export default function RegisterAccountModal({ open, editAccount, onClose, onRegister }) {
  const isEdit = Boolean(editAccount);
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (open) setValues(isEdit ? { name: editAccount.name } : initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editAccount?.account_id]);

  function reset() {
    setValues(initialValues);
    setErrors({});
    setSubmitting(false);
    setSuccess(null);
    setDirty(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !success, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!values.name.trim()) {
      setErrors({ name: "Account name is required." });
      return;
    }

    setSubmitting(true);
    try {
      const account = await onRegister({ name: values.name.trim() });
      setSuccess(account);
    } catch (err) {
      if (err?.fieldErrors) {
        setErrors(err.fieldErrors);
      } else {
        setErrors({ name: err?.message || "Save failed. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal open={open} title={isEdit ? "Rename Account" : "Register Account"} onClose={requestClose}>
        {success ? (
          <div className="flex flex-col gap-4">
            <Alert variant="success" icon={CheckCircle2} title={`"${success.name}" ${isEdit ? "renamed" : "registered"}`}>
              {isEdit ? "The Account switcher and lists now show the new name." : "It's now selected in the Account switcher and ready for Projects."}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            <FormField label="Account name" required error={Boolean(errors.name)} errorText={errors.name}>
              <Input
                value={values.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. Northwind Retail"
                autoFocus
              />
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="primary" loading={submitting}>
                {isEdit ? "Rename Account" : "Register Account"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RegisterAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  editAccount: PropTypes.shape({ account_id: PropTypes.number, name: PropTypes.string }),
  onClose: PropTypes.func.isRequired,
  onRegister: PropTypes.func.isRequired,
};
